import React, { FC, useState } from "react";
import { Flex, Box, Text, SystemProps } from "flicket-ui";
import styled from "styled-components";

const Nav = styled(Box).attrs({ as: "nav" })`
  position: relative;

  &:after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    height: 1px;
    width: 100%;
    background-color: ${(p) => p.theme.colors.N200};
  }
`;

const Ul = styled(Flex).attrs({
  as: "ul",
  position: "relative",
  justifyContent: "flex-start",
  width: 1,
})<{ index: number; tabWidth: string }>`
  list-style: none;
  flex-wrap: wrap;
`;

const Li = styled(Text).attrs({
  as: "li",
  mr: 5,
  py: 2,
  lineHeight: "100%",
})<{ active?: boolean; tabWidth: string; activeColor?: string }>`
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  user-select: none;
  cursor: pointer;

  font-weight: ${(p) => p.theme.fontWeights.regular};
  font-size: ${(p) => p.theme.fontSizes[3]};
  color: ${(p) => p.theme.colors.N600};
  position: relative;

  ${(p) =>
    p.active &&
    `
      color: ${p.theme.colors.N800};
      font-weight: ${p.theme.fontWeights.demiBold};
  `};

  &:after {
    content: "";

    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 2;

    height: 2px;
    width: 100%;

    background: linear-gradient(
      to left,
      ${(p) => p.theme.colors.P300},
      ${(p) => p.theme.colors.P300}
    );
    background-size: ${(p) => (p.active ? "100% 100%" : "0% 100%")};
    background-position: 50% 50%;
    background-repeat: no-repeat;

    transition: all 0.2s ease-in-out;
  }
`;

interface TabItem {
  label: React.ReactNode;
  key: string;
}

interface Props {
  value?: number | string;
  options: (string | TabItem)[];
  onChange: (index: string | number, tabKey?: string) => void;
  initialIndex?: number;
  activeColor?: string;
  size?: string;
}

function isTabItemType(item: string | TabItem): item is TabItem {
  return typeof item !== "string";
}

export const Tabs: FC<Props & SystemProps> = ({
  value: controlledValue,
  onChange,
  options,
  initialIndex,
  activeColor,
  size = "M",
  ...props
}) => {
  const [uncontrolledIndex, setUncontrolledIndex] = useState(initialIndex || 0);

  const isControlled = typeof controlledValue !== "undefined";

  let controlledIndex: number | undefined;
  if (isControlled) {
    // Index value
    if (
      typeof controlledValue === "number" &&
      Number.isInteger(controlledValue)
    ) {
      controlledIndex = controlledValue;
    }
    // Key value
    else {
      controlledIndex = options.findIndex((item) =>
        typeof item === "string"
          ? item === controlledValue
          : item.key === controlledValue
      );
    }
  }

  const tabIndex = isControlled ? controlledIndex : uncontrolledIndex;

  const tabWidth = `${100 / options.length}%`;

  return (
    <Nav width={1}>
      <Box width={1} {...props}>
        <Ul index={tabIndex} tabWidth={tabWidth}>
          {options.map((item, index) => {
            const isTabItem = isTabItemType(item);
            const tabKey = isTabItem ? item.key : item;
            const elementKey = isTabItem ? item.key : `${item}-${index}`;
            const label = isTabItem ? item.label : item;

            return (
              <Li
                active={tabIndex === index}
                activeColor={activeColor}
                tabWidth={tabWidth}
                onClick={() => {
                  onChange(index, tabKey);
                  setUncontrolledIndex(index);
                }}
                key={elementKey}
                // variant={`extraBold.${size}` as any}
              >
                {label}
              </Li>
            );
          })}
        </Ul>
      </Box>
    </Nav>
  );
};
